import { Heart, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export interface Material {
  id: string;
  name: string;
  category: string;
  price: number;
  priceUnit: string;
  image: string;
  colors: string[];
  texture: string;
  isFavorite?: boolean;
}

interface MaterialCardProps {
  material: Material;
  onFavorite?: (id: string) => void;
  onAddToProject?: (id: string) => void;
  onClick?: (material: Material) => void;
}

export function MaterialCard({ material, onFavorite, onAddToProject, onClick }: MaterialCardProps) {
  return (
    <div
      className="group relative overflow-hidden rounded-xl bg-card shadow-card transition-all duration-500 ease-out hover:shadow-card-hover hover:-translate-y-2 cursor-pointer"
      onClick={() => onClick?.(material)}
    >
      {/* Image */}
      <div className="aspect-square overflow-hidden">
        <img
          src={material.image}
          alt={material.name}
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
        />
        
        {/* Overlay Actions */}
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/70 via-foreground/20 to-transparent opacity-0 transition-all duration-500 group-hover:opacity-100" />
        
        <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between opacity-0 transition-all duration-500 translate-y-4 group-hover:opacity-100 group-hover:translate-y-0">
          <Button
            size="sm"
            variant="secondary"
            className="gap-1.5 bg-background/95 hover:bg-background text-foreground transition-all duration-300 hover:scale-105 active:scale-95"
            onClick={(e) => {
              e.stopPropagation();
              onAddToProject?.(material.id);
            }}
          >
            <Plus className="h-4 w-4 transition-transform duration-300 group-hover:rotate-90" />
            Add to Project
          </Button>
          <Button
            size="icon"
            variant="secondary"
            className={cn(
              "h-9 w-9 bg-background/95 hover:bg-background transition-all duration-300 hover:scale-110 active:scale-95",
              material.isFavorite && "text-destructive"
            )}
            onClick={(e) => {
              e.stopPropagation();
              onFavorite?.(material.id);
            }}
          >
            <Heart className={cn(
              "h-4 w-4 transition-transform duration-300",
              material.isFavorite ? "fill-current scale-110" : "hover:scale-125"
            )} />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 transition-all duration-300 group-hover:bg-muted/30">
        <div className="mb-2 flex items-start justify-between gap-2">
          <h3 className="font-serif text-lg font-medium text-foreground line-clamp-1 transition-colors duration-300 group-hover:text-primary">
            {material.name}
          </h3>
          <Badge variant="secondary" className="shrink-0 text-xs transition-all duration-300 group-hover:bg-primary/10 group-hover:text-primary">
            {material.category}
          </Badge>
        </div>
        
        <p className="text-sm text-muted-foreground mb-3 transition-colors duration-300">
          {material.texture} texture
        </p>

        <div className="flex items-center justify-between">
          <div className="flex gap-1">
            {material.colors.slice(0, 4).map((color, i) => (
              <div
                key={i}
                className="h-4 w-4 rounded-full border border-border shadow-sm transition-all duration-300 hover:scale-125 hover:shadow-md"
                style={{ backgroundColor: color, transitionDelay: `${i * 50}ms` }}
              />
            ))}
            {material.colors.length > 4 && (
              <span className="flex h-4 w-4 items-center justify-center text-[10px] text-muted-foreground">
                +{material.colors.length - 4}
              </span>
            )}
          </div>
          <p className="font-medium text-foreground transition-all duration-300 group-hover:text-primary">
            ${material.price}<span className="text-xs text-muted-foreground">/{material.priceUnit}</span>
          </p>
        </div>
      </div>
    </div>
  );
}
